import { useState } from "react";
import { PlusIcon, XIcon } from "lucide-react";

import {
  WHERE_OPERATOR_LABELS,
  WHERE_OPERATOR_LABELS_NEGATED,
  isWhereGroup,
  valueAsSaid,
  type ReportSource,
  type ReportSourceField,
  type WhereGroup,
  type WhereRule,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  WORD_INPUT_CLASS,
  Word,
  definitionsForSource,
  fieldsByWhatTheyHold,
  type WordOption,
} from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidget.tools";
import { Icon } from "@/components/atoms/Icon";
import { getFilterDefinitionsForObject } from "@/app/app/_/views/RulesView/utils/filterDefinitions";
import type {
  FilterDefinitionType,
  ReferenceValueOptionsType,
} from "@/components/@types";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { useNodeListQuery } from "@/components/organisms/Node/Node.hooks";
import { Popper } from "@/components/atoms/Popper";
import { useScope } from "@/hooks/useScope";
import { getReferenceValueOptionsByTypename, Objects } from "@/objects";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/**
 * The comparisons a widget is narrowed by, written as the sentence they are.
 *
 * A row is not a form: it is "amount is over $52", in the same live words the
 * card's own question is made of, and the group it sits in says how its rows
 * are taken together. Written as boxed fields instead, two comparisons cost
 * six bordered boxes and read as a different application from the card they
 * belong to.
 */
export function ReportsViewWidgetWhereEditor({
  source,
  group,
  onChange,
  depth = 0,
  recordsBy = "name",
  leadWord = "Where",
}: {
  source: ReportSource | undefined;
  group: WhereGroup;
  onChange: (group: WhereGroup) => void;
  depth?: number;
  /**
   * How a comparison against a record keeps it: by the name a summary groups
   * it by, or - where the record itself is matched, as a rule's "When" is -
   * by its identifier, shown by its name.
   */
  recordsBy?: "name" | "identifier";
  /** What the first line opens with - nothing where a heading above already says it. */
  leadWord?: string | null;
}) {
  const fields = source?.fields ?? [];
  const changeTerm = (index: number, term: WhereRule | WhereGroup | null) =>
    onChange({
      ...group,
      terms:
        term === null
          ? group.terms.filter((_, at) => at !== index)
          : group.terms.map((existing, at) => (at === index ? term : existing)),
    });

  return (
    <div
      data-testid={depth === 0 ? "widget-where-editor" : "widget-where-group"}
      // Lines of one sentence, each the height of a line, set apart by enough
      // to read one at a time.
      className="flex flex-col gap-1.5 min-w-0"
    >
      {/* A group says how its own rows are taken together, above them: inside
          one, a row is not joined to the row before it - they are all held by
          the group, and "all of" or "any of" is what holds them. */}
      {depth > 0 && (
        <div className="flex items-baseline gap-1">
          <Word
            value={group.connector}
            options={[
              {
                value: "all",
                label: "all of",
                note: "Every condition below has to hold",
              },
              {
                value: "any",
                label: "any of",
                note: "One of them below is enough",
              },
            ]}
            isMidSentence={false}
            onChange={(connector) =>
              onChange({ ...group, connector: connector as "all" | "any" })
            }
          />
        </div>
      )}

      {group.terms.map((term, index) => (
        <div
          key={index}
          className={cn(
            "flex items-baseline gap-2.5 min-w-0",
            // The button that removes a row is not part of reading it: it
            // waits until the row is under the pointer.
            "[&:hover_.where-row-remove]:opacity-100",
          )}
        >
          {depth === 0 && (
            <Joiner
              group={group}
              index={index}
              leadWord={leadWord}
              onChange={(connector) => onChange({ ...group, connector })}
            />
          )}
          <div className="flex-1 min-w-0">
            {isWhereGroup(term) ? (
              <div
                // The rule stands between the joiner and the group's rows with
                // the same air on either side of it - the word carries a
                // hair of its own padding, so this is a shade under the gap.
                className="pl-1.75 [border-left-width:1px] [border-left-style:solid] border-border"
              >
                <ReportsViewWidgetWhereEditor
                  source={source}
                  group={term}
                  depth={depth + 1}
                  recordsBy={recordsBy}
                  onChange={(next) => changeTerm(index, next)}
                />
              </div>
            ) : (
              <Comparison
                rule={term}
                fields={fields}
                source={source}
                recordsBy={recordsBy}
                onChange={(next) => changeTerm(index, next)}
              />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon-xs"
            className="where-row-remove opacity-[0] [transition:opacity_0.12s_ease] shrink-0"
            aria-label="Remove condition"
            onClick={() => changeTerm(index, null)}
          >
            <Icon ReactIcon={XIcon} size="sm" />
          </Button>
        </div>
      ))}

      {/* Set below the lines it adds to, rather than among them - and only
          held off them when there are lines to be held off. */}
      <div
        className={cn(
          "flex items-center gap-3",
          group.terms.length ? "pt-1" : "pt-0",
        )}
      >
        <Add
          label="Condition"
          onClick={() =>
            onChange({
              ...group,
              terms: [...group.terms, emptyComparison(fields)],
            })
          }
        />
        <Add
          label="Condition group"
          onClick={() =>
            onChange({
              ...group,
              terms: [
                ...group.terms,
                { connector: "any", terms: [emptyComparison(fields)] },
              ],
            })
          }
        />
      </div>
    </div>
  );
}

/**
 * One more line, or one more group of them.
 *
 * Two buttons rather than one that asks which: there are two things to add,
 * each said in a word, and a menu in front of them spends a click on a
 * question the buttons themselves answer.
 */
function Add({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 [border:none] bg-none [background-position-x:initial] [background-position-y:initial] bg-size-[initial] [background-repeat:initial] [background-attachment:initial] [background-origin:initial] [background-clip:initial] bg-[initial] cursor-pointer pl-1 pr-1 pt-0.5 pb-0.5 -ml-1 -mr-1 rounded-sm font-[inherit] text-sm text-muted-foreground/80 [transition:background-color_0.12s_ease] hover:bg-muted hover:text-muted-foreground active:bg-accent"
    >
      <Icon ReactIcon={PlusIcon} size="xs" />
      {label}
    </button>
  );
}

/** What the row before it has to do with this one. */
function Joiner({
  group,
  index,
  leadWord,
  onChange,
}: {
  group: WhereGroup;
  index: number;
  leadWord: string | null;
  onChange: (connector: "all" | "any") => void;
}) {
  const said = group.connector === "any" ? "or" : "and";
  if (index === 0) {
    if (!leadWord) return null;
    return (
      <Typography level="body-md" className="text-muted-foreground/80 shrink-0">
        {leadWord}
      </Typography>
    );
  }
  // One row carries the choice and the rest follow it: a group is joined one
  // way throughout, and offering the choice on every row invites the belief
  // that it is not.
  if (index > 1) {
    return (
      <Typography level="body-md" className="text-muted-foreground/80 shrink-0">
        {said}
      </Typography>
    );
  }
  return (
    <div className="shrink-0">
      <Word
        value={group.connector}
        options={[
          { value: "all", label: "and", note: "Every condition has to hold" },
          { value: "any", label: "or", note: "Any one of them is enough" },
        ]}
        onChange={(connector) => onChange(connector as "all" | "any")}
      />
    </div>
  );
}

/** One comparison, as a line of the sentence. */
function Comparison({
  rule,
  fields,
  source,
  recordsBy,
  onChange,
}: {
  rule: WhereRule;
  fields: ReportSourceField[];
  source: ReportSource | undefined;
  recordsBy: "name" | "identifier";
  onChange: (rule: WhereRule) => void;
}) {
  const field = fields.find((one) => one.name === rule.field);
  const comparisons = comparisonsFor(field, recordsBy);
  const chosen = `${rule.operator}${rule.isNegated ? ":not" : ""}`;
  // A column that points at records is compared against one of those records,
  // which the record type itself knows how to list. The column says what it
  // points at; the record type's own filters are read only for a source whose
  // columns predate that. Asked the other way round, a relation the record
  // does not offer as a filter - a transaction's charted account - was a line
  // of text to be typed out by hand.
  const referenced =
    rule.operator === "eq"
      ? (field?.typename ??
        definitionsForSource(source)[rule.field.split("__")[0]]
          ?.referenceTypename)
      : undefined;

  return (
    <div className="inline-flex items-baseline gap-1.5 flex-wrap">
      <Word
        value={rule.field}
        options={fieldsByWhatTheyHold(fields, source)}
        onChange={(name) => {
          const next = fields.find((one) => one.name === name);
          // A comparison that means nothing for the new column is not kept:
          // "contains" has no meaning for an amount.
          const keeps = comparisonsFor(next, recordsBy).some(
            (one) => one.value === chosen,
          );
          // Nor is what was being compared against. A contact is not a
          // charted account and neither is a sum of money, so the value goes
          // with the column it belonged to rather than sitting under the new
          // one looking like an answer.
          onChange({
            ...rule,
            field: name,
            value: null,
            ...(keeps ? {} : { operator: "eq", isNegated: false }),
          });
        }}
      />
      <Word
        value={chosen}
        options={comparisons}
        onChange={(picked) => {
          const [operator, negation] = picked.split(":");
          onChange({ ...rule, operator, isNegated: negation === "not" });
        }}
      />
      {rule.operator !== "is_null" &&
        (referenced ? (
          <ComparedWithRecord
            typename={referenced}
            value={rule.value}
            recordsBy={recordsBy}
            onChange={(value) => onChange({ ...rule, value })}
          />
        ) : (
          <ComparedWith
            field={field}
            value={rule.value}
            onChange={(value) => onChange({ ...rule, value })}
          />
        ))}
    </div>
  );
}

/**
 * A record to compare against, picked from the records there are.
 *
 * A summary groups a relation by the name it goes by, so what is compared is
 * that name - and the record type knows how to list its own, which is what a
 * reference field is picked from everywhere else in the app.
 */
function ComparedWithRecord({
  typename,
  value,
  recordsBy,
  onChange,
}: {
  typename: string;
  value: unknown;
  recordsBy: "name" | "identifier";
  onChange: (value: unknown) => void;
}) {
  const isOpenState = useState(false);
  const [, setIsOpen] = isOpenState;
  const { activeEntityIdentifiers } = useScope();
  const object = Object.values(Objects).find(
    (one) => one.typename === typename,
  );
  const listed = getReferenceValueOptionsByTypename(typename)(
    activeEntityIdentifiers,
    "filter",
  );
  const said =
    value === undefined || value === null || value === "" ? "" : String(value);

  const anchor = (
    <span
      data-testid="widget-where-record"
      onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
      className={cn(
        "inline text-sm [font-weight:600] text-foreground rounded-sm pl-0.5 pr-0.5 -ml-0.5 -mr-0.5 cursor-pointer underline [text-decoration-thickness:initial] decoration-dotted decoration-border underline-offset-[3px] [transition:background-color_0.12s_ease] hover:bg-muted active:bg-accent",
        said ? "" : "text-muted-foreground/80 font-medium",
      )}
    >
      {said && recordsBy === "identifier" && listed ? (
        <RecordName
          listed={listed}
          identifier={said}
          fallback={object?.getTitle}
        />
      ) : (
        said || "…"
      )}
    </span>
  );

  if (!listed) {
    return anchor;
  }

  return (
    <Popper anchor={anchor} isOpenState={isOpenState} placement="bottom-start">
      <div className="min-w-65 max-h-80 flex">
        <NodeList
          {...listed}
          fillHeight={false}
          onSelectOption={(
            record: {
              name?: string | null;
              identifier?: string | null;
            } & Record<string, unknown>,
          ) => {
            onChange(
              recordsBy === "identifier"
                ? (record?.identifier ?? null)
                : (record?.name ?? object?.getTitle?.(record as never) ?? null),
            );
            setIsOpen(false);
          }}
          className="max-w-none"
        />
      </div>
    </Popper>
  );
}

/**
 * A record kept by its identifier, said by its name: looked up in the list its
 * picker shows, which the cache already holds once the picker has opened.
 */
function RecordName({
  listed,
  identifier,
  fallback,
}: {
  listed: ReferenceValueOptionsType<unknown>;
  identifier: string;
  fallback?: (record: never) => string | null | undefined;
}) {
  const { options } = useNodeListQuery<unknown>(listed);
  const record = options.find(
    (option) => (option as { identifier?: string }).identifier === identifier,
  ) as { name?: string | null } | undefined;
  if (!record) return <>…</>;
  return <>{record.name ?? fallback?.(record as never) ?? identifier}</>;
}

/**
 * What a comparison is compared against.
 *
 * It reads as the column reads - $90,000 rather than 90000 - until it is
 * being typed, when it is the digits again: a thousands separator under a
 * cursor is something to delete.
 */
function ComparedWith({
  field,
  value,
  onChange,
}: {
  field: ReportSourceField | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const [typing, setTyping] = useState<string | null>(null);
  const isNumber = field?.kind === "money";
  const said = valueAsSaid(value, field);

  return (
    <input
      type={field?.kind === "date" ? "date" : "text"}
      value={typing ?? (said || "")}
      placeholder="…"
      onFocus={() =>
        setTyping(value === undefined || value === null ? "" : String(value))
      }
      onBlur={() => setTyping(null)}
      inputMode={isNumber ? "decimal" : undefined}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        const written = event.target.value;
        // An amount is compared against an amount: a column of money has no
        // answer to "is over shell", so the letters never land in the first
        // place rather than being refused once the whole row is written.
        const taken = isNumber ? written.replace(/[^\d.-]/g, "") : written;
        setTyping(taken);
        if (taken === "") return onChange(null);
        const asNumber = Number(taken);
        onChange(isNumber ? (Number.isNaN(asNumber) ? null : asNumber) : taken);
      }}
      onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
      // A value is a word of the line, so it is written as one: the app's own
      // input brings a two-rem minimum height with it, which stood a dozen
      // pixels taller than the words beside it and pushed the rows apart.
      // Underlined the way every other word of the line is underlined - by
      // the text's own decoration rather than by a border under the box.
      // The two draw different dots at different heights, and a line made
      // of both reads as two lines.
      className={cn(WORD_INPUT_CLASS, "min-w-10")}
      style={{
        width: `${Math.max(4, (typing ?? said ?? "").length + 1)}ch`,
      }}
    />
  );
}

/** A new row, on whichever column the source offers first. */
function emptyComparison(fields: ReportSourceField[]): WhereRule {
  return { field: fields[0]?.name ?? "", operator: "eq", value: null };
}

/** What a column can be compared with, said as the sentence says it. */
function comparisonsFor(
  field: ReportSourceField | undefined,
  recordsBy: "name" | "identifier" = "name",
): WordOption[] {
  // A record kept as itself is that record or not: its name is not there to
  // contain anything.
  const keys =
    recordsBy === "identifier" && field?.typename
      ? ["eq", "eq:not", "is_null", "is_null:not"]
      : field?.kind === "money" || field?.kind === "date"
        ? ["eq", "eq:not", "gt", "gte", "lt", "lte", "is_null", "is_null:not"]
        : [
            "eq",
            "eq:not",
            "contains",
            "contains:not",
            "starts_with",
            "is_null",
            "is_null:not",
          ];
  return keys.map((key) => {
    const [operator, negation] = key.split(":");
    return {
      value: key,
      label:
        negation === "not"
          ? (WHERE_OPERATOR_LABELS_NEGATED[operator] ??
            `is not ${WHERE_OPERATOR_LABELS[operator]}`)
          : (WHERE_OPERATOR_LABELS[operator] ?? operator),
    };
  });
}
